import React from 'react';
import { 
  Plane, 
  Home, 
  Heart, 
  Truck, 
  Sparkles, 
  Palette, 
  TreePine, 
  Users 
} from 'lucide-react';

const Services: React.FC = () => {
  const tenantServices = [
    {
      icon: Plane,
      title: 'Airport Transfer',
      description: 'Comfortable and reliable pickup and drop-off service from/to Chiang Mai Airport.',
      features: ['24/7 Availability', 'Professional Drivers', 'Meet & Greet Service']
    },
    {
      icon: Home,
      title: 'Temporary Housing',
      description: 'Comfortable short-term accommodation while you search for your perfect home.',
      features: ['Fully Furnished', 'Flexible Duration', 'Prime Locations']
    },
    {
      icon: Heart,
      title: 'Pet Care Services',
      description: 'Professional pet placement and care services for your beloved companions.',
      features: ['Pet-Friendly Housing', 'Veterinary Connections', 'Pet Sitting']
    },
    {
      icon: Truck,
      title: 'Moving Assistance',
      description: 'Complete moving support to make your relocation smooth and stress-free.',
      features: ['Packing Services', 'Transportation', 'Setup Assistance']
    }
  ];

  const landlordServices = [
    {
      icon: Sparkles,
      title: 'House Cleaning',
      description: 'Professional deep cleaning services to keep your properties pristine.',
      features: ['Deep Cleaning', 'Regular Maintenance', 'Move-in/Move-out Cleaning']
    },
    {
      icon: Palette,
      title: 'Interior Decoration',
      description: 'Transform your property with modern, appealing interior design services.',
      features: ['Staging Services', 'Furniture Selection', 'Color Consultation']
    },
    {
      icon: TreePine,
      title: 'Garden Maintenance',
      description: 'Keep your outdoor spaces beautiful with our professional landscaping.',
      features: ['Landscape Design', 'Regular Upkeep', 'Seasonal Planting']
    }
  ];

  const ServiceCard = ({ service, type }: { service: any; type: 'tenant' | 'landlord' }) => (
    <div className="service-card-hover bg-white rounded-2xl shadow-lg p-6 sm:p-8 border border-gray-100">
      <div className={`inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-xl mb-4 sm:mb-6 ${
        type === 'tenant' ? 'bg-primary-100 text-primary-600' : 'bg-accent-100 text-accent-600'
      }`}>
        <service.icon size={28} className="sm:w-8 sm:h-8" />
      </div>
      
      <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">{service.title}</h3>
      <p className="text-gray-600 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">{service.description}</p>
      
      <ul className="space-y-2 sm:space-y-3">
        {service.features.map((feature: string, index: number) => (
          <li key={index} className="flex items-center text-gray-700 text-sm sm:text-base">
            <div className={`w-2 h-2 rounded-full mr-3 flex-shrink-0 ${
              type === 'tenant' ? 'bg-primary-500' : 'bg-accent-500'
            }`}></div>
            {feature}
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <section id="services" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16 px-4">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            Comprehensive Real Estate Services
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Whether you're a tenant looking for your perfect home or a landlord wanting to maximize 
            your property's potential, I provide personalized services tailored to your needs.
          </p>
        </div>

        {/* Tenant Services */}
        <div className="mb-20">
          <div className="flex items-center justify-center mb-12">
            <div className="flex items-center space-x-3">
              <Users className="text-primary-600" size={32} />
              <h3 className="text-3xl font-bold text-gray-900">For Tenants</h3>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 sm:gap-8">
            {tenantServices.map((service, index) => (
              <ServiceCard 
                key={index} 
                service={service} 
                type="tenant" 
              />
            ))}
          </div>
        </div>

        {/* Landlord Services */}
        <div>
          <div className="flex items-center justify-center mb-12">
            <div className="flex items-center space-x-3">
              <Home className="text-accent-600" size={32} />
              <h3 className="text-3xl font-bold text-gray-900">For Landlords</h3>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 sm:gap-8">
            {landlordServices.map((service, index) => (
              <ServiceCard 
                key={index} 
                service={service} 
                type="landlord" 
              />
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-2xl mx-auto">
            <h4 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Get Started?
            </h4>
            <p className="text-gray-600 mb-6">
              Contact me today to discuss how I can help with your real estate needs in Chiang Mai.
            </p>
            <a
              href="#contact"
              className="inline-block bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Schedule Consultation
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services; 